import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../services/api';
import { analytics } from '../services/analytics';
import type { JournalEntryDraft, JournalPrivacy, JournalPrompts } from '../types/auth';
import { resolveReflectionStep, type ReflectionStep } from '../components/reflection/reflectionFlow';
import { useReportReflectionVisible } from '../components/reflection/reflectionLauncher';

/**
 * CORE-1751 — откуда открыто окно: после практики из библиотеки, после выхода
 * из Live-сессии или кнопкой в сообщении Агапеллы. Аналитика одна и та же, а
 * источник различает три пути, по которым человек приходит к рефлексии.
 */
export type ReflectionSource = 'practice' | 'live_exit' | 'chat_offer';

/**
 * CORE-1455 — рефлексия после практики: что загрузить, что показать, что считать.
 *
 * Хук держит три вещи вместе — вопросы, выбор приватности и события аналитики, —
 * потому что порознь они рассыпаются по экрану и порядок между ними теряется.
 *
 * Ничего не грузится заранее. Запрос уходит, когда практика уже засчитана: до
 * этого момента вопросы и настройка никому не нужны, а человек, который бросит
 * материал на середине, не должен за них платить.
 */
interface Args {
  /** Есть ли сессия — гостю дневник не предлагаем. */
  isAuthenticated: boolean;
  /** Идентификатор завершения, если он известен: запись привязывается к практике. */
  practiceCompletionId?: number | null;
  /**
   * CORE-1751 — какой материал открыт (`тип:id`). Экран практики переживает
   * переход к другой практике, и без этого ключа «одно открытие» считалось бы
   * на экран, а не на практику: досмотревший вторую рефлексии уже не получал.
   */
  materialKey: string;
  /**
   * CORE-1751 — ответ /completed ещё в пути, и `practiceCompletionId` может
   * вот-вот появиться. Пока так, открытие окна не отчитывается: иначе оно ушло
   * бы дважды — без id и с ним.
   */
  completionPending?: boolean;
  /** CORE-1751 — источник открытия для аналитики; по умолчанию — практика. */
  source?: ReflectionSource;
}

export function usePracticeReflection({
  isAuthenticated, practiceCompletionId, materialKey, completionPending = false, source = 'practice',
}: Args) {
  const [prompts, setPrompts] = useState<JournalPrompts | undefined>();
  const [privacy, setPrivacy] = useState<JournalPrivacy | undefined>();
  const [open, setOpen] = useState(false);
  const [savingPrivacy, setSavingPrivacy] = useState(false);
  // Одна практика — одно открытие. Перемотка назад и повторное завершение
  // второго окна не дают.
  const openedRef = useRef(false);
  // CORE-1751 — запись сохранена. Закрытие окна после этого — не отказ от
  // рефлексии: до правки «Готово» после сохранения считалось пропуском.
  const savedRef = useRef(false);
  // CORE-1751 — открытие окна уже посчитано. Одно открытие — одно событие.
  const openedTrackedRef = useRef(false);

  // Другая практика — снова «ещё не открывали». Окно прежней закрывается: id её
  // завершения трекер уже сбросил, и запись ушла бы без привязки.
  useEffect(() => {
    openedRef.current = false;
    savedRef.current = false;
    openedTrackedRef.current = false;
    setOpen(false);
  }, [materialKey]);

  // Настройка приезжает вместе с выбором приватности — одним запросом, чтобы
  // окно не мигало.
  const step: ReflectionStep = open
    ? resolveReflectionStep({
      signedIn: isAuthenticated,
      reflectionEnabled: privacy?.reflectionEnabled ?? false,
      privacy,
    })
    : 'none';

  /** Практика засчитана — сюда приходит колбэк трекера завершения. */
  const onPracticeCompleted = useCallback(() => {
    // Настройку здесь ещё не знаем — она приедет вместе с приватностью. Это
    // осознанно: спрашивать её заранее значило бы ходить в сеть за каждого,
    // кто открыл практику, включая тех, кто её бросит.
    if (openedRef.current || !isAuthenticated) return;
    openedRef.current = true;
    openedTrackedRef.current = false;
    setOpen(true);
  }, [isAuthenticated]);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    // Обе загрузки независимы: без вопросов окно бессмысленно, без настройки
    // неизвестно, какое из двух показать. Падение любой — окно не открываем,
    // а не показываем пустое.
    void (async () => {
      try {
        const [loadedPrompts, loadedPrivacy] = await Promise.all([
          api.getJournalPrompts(),
          api.getJournalPrivacy(),
        ]);
        if (!alive) return;
        setPrompts(loadedPrompts);
        setPrivacy(loadedPrivacy);
      } catch {
        if (alive) setOpen(false);
      }
    })();
    return () => { alive = false; };
  }, [open]);

  // CORE-1751 — окно на экране (любой шаг, включая «Сохранено»): поздравление
  // по Пути Света ждёт, пока его закроют. Открытое, но выключенное (рефлексия
  // выключена в настройках) поздравление не держит — иначе выключившему
  // рефлексию оно не пришло бы никогда.
  //
  // Возврат QA 23.10 — а пока вопросы и настройка грузятся, держит: окно вот-вот
  // появится, и поздравление, уже лежащее в очереди (после Live-сессии оно
  // начислено к её концу), иначе мелькало бы перед ним. Загрузка кончается
  // всегда — данными или закрытием, — так что навсегда не держит и она.
  const loading = open && (prompts === undefined || privacy === undefined);
  useReportReflectionVisible(step !== 'none' || loading);

  // CORE-1455 — открытие экрана знает только клиент: сервер о нём не узнаёт
  // вовсе. Сохранение и смену приватности эмитит он сам, и дублировать их
  // отсюда нельзя — иначе одно действие посчитается дважды.
  //
  // CORE-1751 — ровно одно событие на открытие. Раньше эффект срабатывал и на
  // приход id завершения: когда приватность уже выбрана, вопросы открываются
  // раньше ответа /completed, и на DEV 19.10 каждое такое открытие ушло дважды —
  // с `practice_completion_id: null` и с настоящим. Теперь событие ждёт ответа
  // (`completionPending`) и уходит один раз — с тем id, который есть.
  useEffect(() => {
    if (step !== 'questions' || completionPending || openedTrackedRef.current) return;
    openedTrackedRef.current = true;
    analytics.track('journal_opened', {
      practice_completion_id: practiceCompletionId ?? null,
      source,
    });
  }, [step, practiceCompletionId, completionPending, source]);

  const choosePrivacy = useCallback(async (aiMayRead: boolean) => {
    setSavingPrivacy(true);
    try {
      const saved = await api.setJournalPrivacy(aiMayRead);
      setPrivacy(saved);
    } catch {
      // Не сохранилось — остаёмся на окне выбора. Провалиться дальше значило
      // бы записать первую рефлексию с уровнем, которого человек не выбирал.
    } finally {
      setSavingPrivacy(false);
    }
  }, []);

  const save = useCallback(async (draft: JournalEntryDraft) => {
    await api.createJournalEntry(draft);
    savedRef.current = true;
  }, []);

  const close = useCallback(() => {
    // Закрытие без записи — тоже сигнал, и знает о нём только клиент. Закрытие
    // ПОСЛЕ записи — нет: сохранение сервер уже посчитал сам.
    if (step === 'questions' && !savedRef.current) {
      analytics.track('journal_skipped', {
        practice_completion_id: practiceCompletionId ?? null,
        source,
      });
    }
    setOpen(false);
  }, [step, practiceCompletionId, source]);

  return { step, prompts, privacy, savingPrivacy, onPracticeCompleted, choosePrivacy, save, close };
}
